import { ref, h, defineAsyncComponent, type Ref, type ComputedRef } from 'vue';

import { useSlideout } from '~/composables/utils/useSlideout';
import { useQuantity } from '~/modules/product/composables/useQuantity';
import useSelectedPlan from '~/modules/worry-no-more/composables/useSelectedPlan';
import { useWnm } from '~/modules/worry-no-more/composables/useWnm';
import usePlans from '~/modules/worry-no-more/composables/usePlans';
import { WNM_SLIDEOUT_DEFAULTS, type ProtectionPlanType } from '~/modules/worry-no-more/constants';
import { fireDrawerOpenTag, firePlanSaveTag } from '~/modules/worry-no-more/analytics/useWnmAnalytics';
import type { ParsedPlan } from '~/modules/worry-no-more/services/PlansService';
import type { BasicSlideoutType } from '~/stores/useSlideout';

export type WnmSlideoutOptions = {
  onClose?: () => void;
  // in case plans fail to load for ATB slideout
  skipErrorSlideout?: boolean;
};

const WorryNoMoreBody = defineAsyncComponent(() => import('../components/slideout/slideout-body/WorryNoMore.vue'));
const WorryNoMoreFooter = defineAsyncComponent(() => import('../components/slideout/slideout-footer/WorryNoMoreFooter.vue'));

function buildErrorSlideoutProps(): Partial<BasicSlideoutType> {
  return {
    component: h(
      defineAsyncComponent(() => import('@atomic-ui/notification/Notification.vue')),
      {
        severity: 'error',
        role: 'alert',
        closable: false,
      },
      () => 'Oops—we’re having trouble loading protection plans. You can keep checking out, or refresh to try again.',
    ),
    props: {
      severity: 'error',
      role: 'alert',
      closable: false,
    },
  };
}

function buildSuccessSlideoutProps({
  plansData,
  selectedPlanIdFormValue,
  isProductBigTicket,
  quantity,
  protectionPlans,
  updateSelectedPlanIdByForm,
  setPlanIdEntryByUpc,
  closeSlideout,
}: {
  plansData: ParsedPlan | null;
  selectedPlanIdFormValue: Ref<number>;
  isProductBigTicket: boolean;
  quantity: number;
  protectionPlans: ComputedRef<ProtectionPlanType[] | null>;
  updateSelectedPlanIdByForm: (planId: number) => void;
  setPlanIdEntryByUpc: () => void;
  closeSlideout: () => void;
}): Partial<BasicSlideoutType> {
  const onModelValueUpdate = (val: number) => {
    selectedPlanIdFormValue.value = val;
  };

  const submitForm = () => {
    updateSelectedPlanIdByForm(selectedPlanIdFormValue.value);
    setPlanIdEntryByUpc();

    const savedPlan = protectionPlans.value?.find((p) => p.id === selectedPlanIdFormValue.value);
    firePlanSaveTag(savedPlan ? savedPlan.name : 'continue without protection plan');

    closeSlideout();
  };

  return {
    component: WorryNoMoreBody,
    props: {
      plansData,
      modelValue: selectedPlanIdFormValue.value,
      isBigTicketItem: isProductBigTicket,
      quantity,
      protectionPlans: protectionPlans.value,
    },
    evtHandlers: {
      'update:modelValue': onModelValueUpdate,
    } as Record<string, (...args: unknown[]) => void>,
    footerEvtHandlers: {
      submitForm,
    },
    footerComponent: WorryNoMoreFooter,
    footerProps: {
      contactInfo: plansData?.contactInfo,
      termsAndConditions: plansData?.termsAndConditions,
      detailsLink: plansData?.detailsLink,
    },
  };
}

export function useWnmSlideout() {
  const { showSlideout, closeSlideout } = useSlideout();
  const { selectedPlanId, updateSelectedPlanIdByForm } = useSelectedPlan();
  const { protectionPlans, defaultPlanId, setPlanIdEntryByUpc, isProductBigTicket } = useWnm();
  const { plans, fetch } = usePlans();
  const { quantity } = useQuantity();

  const showLoader = ref(false);

  function openWnmSlideout(options: WnmSlideoutOptions = {}) {
    const { onClose, skipErrorSlideout = false } = options;
    const selectedPlanIdFormValue = ref(selectedPlanId.value ?? defaultPlanId.value);

    fireDrawerOpenTag();
    if (!plans.value) {
      showLoader.value = true;
    }

    let slideoutProps: Partial<BasicSlideoutType> = {};

    fetch()
      .then(() => {
        slideoutProps = buildSuccessSlideoutProps({
          plansData: plans.value,
          selectedPlanIdFormValue,
          isProductBigTicket: isProductBigTicket.value,
          quantity: quantity.value,
          protectionPlans,
          updateSelectedPlanIdByForm,
          setPlanIdEntryByUpc,
          closeSlideout,
        });
      })
      .catch(() => {
        if (skipErrorSlideout) {
          onClose?.();
          return;
        }

        slideoutProps = buildErrorSlideoutProps();
      })
      .finally(() => {
        showLoader.value = false;
        if (slideoutProps.component) {
          showSlideout({
            ...WNM_SLIDEOUT_DEFAULTS,
            ...slideoutProps,
            onClose,
          } as BasicSlideoutType);
        }
      });
  }

  return {
    openWnmSlideout,
    showLoader,
  };
}

export default useWnmSlideout;
